import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { MultiSelectDropdown } from "@admin/components/atoms/Input/MultiSelect";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  copyShifts,
  CopyShiftsDayPayload,
  dateToISO,
  LocationUser,
  Shift,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface Option {
  label: string;
  value: string;
}

interface CopyShiftDays extends ModalProps {
  open: boolean;
  personnelList?: LocationUser[];
  day: Date;
  allShifts?: Shift[];
  companyId: string;
}

export function CopyShiftsDayModal({
  open,
  personnelList = [],
  day,
  allShifts = [],
  companyId,
  ...props
}: CopyShiftDays) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({ mutationFn: copyShifts });
  // const [isCopyOpen, setIsCopyOpen] = useState(true);
  const [, setIsCopyOpen] = useState(true);
  const [selectedPersonnel, setSelectedPersonnel] = useState<LocationUser[]>(
    [],
  );
  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const formattedDate = day.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  const personnelOptions: Option[] = personnelList.map((personnel) => ({
    label: personnel.firstName + " " + personnel.lastName,
    value: personnel.userId,
  }));

  const handleCopyShiftsDay = () => {
    if (selectedPersonnel.length === 0 || !targetDate) {
      toast.error(t("shift.Please select personnel and target date"));
      return;
    }

    const shiftsToCopy = allShifts.filter((shift) => {
      const shiftDate = new Date(
        shift.plannedStartDate ?? "",
      ).toLocaleDateString("en-US");
      const selectedDate = day.toLocaleDateString("en-US");

      return shiftDate === selectedDate;
    });

    if (shiftsToCopy.length === 0) {
      toast.error(t("shift.No shifts found to copy"));
      return;
    }

    const copyStartDate = new Date(day);
    copyStartDate.setHours(0, 0, 0);
    const copyEndDate = new Date(day);
    copyEndDate.setHours(23, 59, 59);

    const assignStartDate = new Date(targetDate);
    assignStartDate.setHours(0, 0, 0);
    const assignEndDate = new Date(targetDate);
    assignEndDate.setHours(23, 59, 59);

    const users = selectedPersonnel.map((personnel) => personnel.userId);

    const payload = {
      copyStartDate: dateToISO(copyStartDate),
      copyEndDate: dateToISO(copyEndDate),
      destinationShiftDates: [
        {
          assignStartDate: dateToISO(assignStartDate),
          assignEndDate: dateToISO(assignEndDate),
        },
      ],
      companyLocationId: personnelList[0]?.companyLocationId || "",
      companyId: companyId,
      users: users,
    } as CopyShiftsDayPayload;

    mutate(payload, {
      onSuccess: () => {
        toast.success(t("shift.The shifts have been copied"));
        queryClient.invalidateQueries({ queryKey: ["shifts"] });
        props.onClose?.();
        clearModal();
      },
      onError: () => {
        toast.error(t("shift.Unable to copy the shifts"));
      },
    });
  };

  const handleDiscard = () => {
    props.onClose?.();
    clearModal();
  };

  const clearModal = () => {
    setSelectedPersonnel([]);
    setTargetDate(null);
    setIsCopyOpen(true);
    setTargetDate(null);
  };

  return (
    <Modal
      open={open}
      title={t("shift.Copy Shifts of to", { date: formattedDate })}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleDiscard}
            disabled={isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            onClick={handleCopyShiftsDay}
            disabled={isPending}
          >
            {t("shift.Copy Shifts")}
          </Button>
        </>
      }
      {...props}
    >
      <div className="flex flex-col w-100 gap-5">
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("common.Select Personnel")}
          </label>
          <div className="flex flex-col w-full">
            <MultiSelectDropdown
              variant="filled"
              placeholder={t("common.Select Personnel")}
              options={personnelOptions}
              selectedOptions={selectedPersonnel.map((personnel) => ({
                label: personnel.firstName + " " + personnel.lastName,
                value: personnel.userId.toString(),
              }))}
              onSelect={(selected) =>
                setSelectedPersonnel(
                  personnelList.filter((personnel) =>
                    selected.some(
                      (option) => option.value === personnel.userId.toString(),
                    ),
                  ),
                )
              }
            />
          </div>
        </div>
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Select Target Days")}
          </label>
          <div className="flex flex-col w-full">
            <Input
              placeholder={t("shift.Standart Day Picker")}
              type="date"
              value={targetDate ? dateToISO(targetDate).substring(0, 10) : ""}
              onChange={(e) => setTargetDate(new Date(e.target.value))}
              min={dateToISO(
                new Date(new Date().setDate(new Date().getDate() + 1)),
              ).substring(0, 10)}
            />
          </div>
        </div>
        {/*
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Copy Open Shifts")}?
          </label>
          <div className="flex flex-col w-full">
            <Select
              options={[
                { label: t("common.Yes"), value: "Yes" },
                { label: t("common.No"), value: "No" },
              ]}
              placeholder={t("shift.Select an option")}
              defaultValue={isCopyOpen ? t("common.Yes") : t("common.No")}
              onChange={(e) => setIsCopyOpen(e.target.value === "Yes")}
            />
          </div>
        </div>
        */}
      </div>
    </Modal>
  );
}
